import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLanguage } from "../../contexts/LanguageContext";
import { X, Bell, CheckCircle, Loader2, Gift, Rocket, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KickstarterSubscribeModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [kickstarterUrl, setKickstarterUrl] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Kickstarter URL from social links
        const socialResponse = await axios.get(`${API}/social-links`);
        if (socialResponse.data?.kickstarter?.url) {
          setKickstarterUrl(socialResponse.data.kickstarter.url);
        }
        
        // Fetch countries list
        const countriesResponse = await axios.get(`${API}/kickstarter/countries`);
        setCountries(countriesResponse.data.countries || []);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === "loading") return;

    setStatus("loading");
    try {
      await axios.post(`${API}/kickstarter/subscribe`, { 
        email,
        first_name: firstName || null,
        country: country || null
      });
      setStatus("success");
      setEmail("");
      setFirstName("");
      setCountry("");
    } catch (error) {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="kickstarter-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#05ce78] to-[#0f9f5f] p-6 text-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            data-testid="modal-close-btn"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Kickstarter Logo - Text styled */}
          <div className="flex justify-center mb-3">
            <span className="text-2xl font-black text-white tracking-tight" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
              Kickstarter
            </span>
          </div>
          
          <h2 className="text-2xl font-bold text-white">
            {language === 'fr' ? 'Bientôt sur Kickstarter !' : 'Coming soon to Kickstarter!'}
          </h2>
          <p className="text-white/90 mt-2">
            {language === 'fr' 
              ? 'Soyez parmi les premiers à soutenir Calli'
              : 'Be among the first to back Calli'}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {status === "success" ? (
            <div className="text-center py-6 space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {language === 'fr' ? 'Inscription réussie !' : 'Successfully subscribed!'}
              </h3>
              <p className="text-gray-600">
                {language === 'fr' 
                  ? 'Vous serez notifié dès le lancement de la campagne Kickstarter.'
                  : 'You will be notified as soon as the Kickstarter campaign launches.'}
              </p>
              {kickstarterUrl && (
                <a
                  href={kickstarterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#05ce78] text-white font-bold rounded-xl hover:bg-[#04b86b] transition-colors"
                >
                  <Rocket className="w-5 h-5" />
                  {language === 'fr' ? 'Voir sur Kickstarter' : 'View on Kickstarter'}
                </a>
              )}
              <button
                onClick={onClose}
                className="block w-full mt-4 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {language === 'fr' ? 'Fermer' : 'Close'}
              </button>
            </div>
          ) : (
            <>
              {/* Benefits */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                  <Gift className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    {language === 'fr' 
                      ? 'Prix préférentiel Early Bird exclusif'
                      : 'Exclusive Early Bird pricing'}
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl">
                  <Rocket className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    {language === 'fr' 
                      ? 'Accès prioritaire et livraison anticipée'
                      : 'Priority access and early delivery'}
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                  <Bell className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    {language === 'fr' 
                      ? 'Notification dès l\'ouverture de la campagne'
                      : 'Notification when the campaign opens'}
                  </span>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email - Required */}
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={language === 'fr' ? 'Votre email *' : 'Your email *'}
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#05ce78] text-gray-900 text-lg"
                    required
                    data-testid="modal-email-input"
                  />
                </div>

                {/* First Name - Optional */}
                <div>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={language === 'fr' ? 'Prénom (optionnel)' : 'First name (optional)'}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#05ce78] text-gray-900"
                    data-testid="modal-firstname-input"
                  />
                </div>

                {/* Country - Optional */}
                <div className="relative">
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#05ce78] text-gray-900 appearance-none bg-white cursor-pointer"
                    data-testid="modal-country-select"
                  >
                    <option value="">{language === 'fr' ? 'Pays (optionnel)' : 'Country (optional)'}</option>
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {language === 'fr' ? c.name_fr : c.name_en}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
                
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full py-4 bg-gradient-to-r from-[#05ce78] to-[#0f9f5f] text-white font-bold text-lg rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="modal-submit-btn"
                >
                  {status === "loading" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Bell className="w-5 h-5" />
                      {language === 'fr' ? 'Me notifier du lancement' : 'Notify me at launch'}
                    </>
                  )}
                </button>

                {status === "error" && (
                  <p className="text-red-500 text-sm text-center">
                    {language === 'fr' ? 'Erreur, veuillez réessayer' : 'Error, please try again'}
                  </p>
                )}

                <p className="text-xs text-gray-500 text-center">
                  {language === 'fr' 
                    ? 'Nous ne partagerons jamais votre email. Désinscription possible à tout moment.'
                    : 'We will never share your email. Unsubscribe at any time.'}
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KickstarterSubscribeModal;
